import type { SocketStatus } from "$types";
import { browser } from "$app/environment";
import { writable } from "svelte/store";
import { dev } from "$app/environment";

export const open_ws_connections = writable<string[]>([]);

const url = "wss://backend.craftroulette.live/websocket";

// these are browser only
export const ws = writable<WebSocket | undefined>(undefined);
export const ws_status = writable<SocketStatus>("disconnected");

const max_reconnect_attempts = 10;
let reconnect_attempts = 0;
let reconnect_timeout: ReturnType<typeof setTimeout>;

const reconnect_web_socket = (token: string, should_refresh_token: boolean) => {
  if (reconnect_attempts >= max_reconnect_attempts) {
    if (dev) console.log("Max reconnect attempts reached. Giving up.");
    ws_status.set("error");
    return;
  }

  reconnect_attempts++;

  if (dev) console.log(`Reconnect attempt ${reconnect_attempts}...`);

  reconnect_timeout = setTimeout(() => {
    const connection = init_web_socket(token, should_refresh_token);
    if (connection) {
      ws.set(connection);
    } else {
      reconnect_web_socket(token, should_refresh_token);
    }
  }, 1000 * reconnect_attempts); // Exponential backoff strategy
};

// Hook this up in the `connection.addEventListener` sections as shown below
const refresh_token = async () => {
  let retries = 4;
  while (retries > 0) {
    // we need to get our access token from the server
    if (dev) {
      console.log(
        "websocket is trying to refresh token...",
        `retries left: ${retries}`,
      );
    }
    const { token }: { token: string } = await (
      await fetch("/api/token_refresh")
    ).json();
    if (token) {
      if (dev) {
        console.log("websocket got new token");
      }
      return token;
    }
    retries--;
  }
};

export const init_web_socket = (
  token: string,
  should_refresh_token: boolean,
): WebSocket | undefined => {
  if (!browser || !token) {
    return undefined;
  }
  let refresher: ReturnType<typeof setInterval>;
  const connection = new WebSocket(url);
  // connection & authentication
  connection.addEventListener("open", () => {
    if (dev) console.log({ event: "onopen" });
    ws_status.set("connecting");

    if (should_refresh_token) {
      refresh_token().then((token) => {
        connection.send(
          JSON.stringify({
            type: "auth",
            access_token: token,
          }),
        );
      });
    } else {
      connection.send(
        JSON.stringify({
          type: "auth",
          access_token: token,
        }),
      );
    }

    refresher = setInterval(
      async () => {
        refresh_token().then((token) => {
          connection.send(
            JSON.stringify({
              type: "auth",
              access_token: token,
            }),
          );
        });
      },
      1000 * 60 * 5, // 5 minutes
    );
  });

  connection.addEventListener("message", (message) => {
    const data = JSON.parse(message.data);
    if (dev && data.type !== "ping") console.log({ event: "onmessage", data });
    // set our status so that we know if we're good to subscribe
    if (data.type === "auth") {
      if (data.status === "ok") {
        reconnect_attempts = 0;
        if (dev) console.log("connected");
        ws_status.set("connected");
      } else {
        ws_status.set("error");
      }
    }

    // keep the connection alive
    if (data.type === "ping") {
      // make our console a little more fun
      if (dev) console.log("ping-pong");
      connection.send(
        JSON.stringify({
          type: "pong",
        }),
      );
    }

    if (data.type === "auth" && data.error?.code === "AUTH_TIMEOUT") {
      clearInterval(refresher);
      // recursively call this function until we get a new token

      ws_status.set("disconnected");
      refresh_token().then((token) => {
        if (!token) {
          ws_status.set("error");
          return;
        }
        init_web_socket(token, false);
      });
    }
  });

  connection.addEventListener("close", () => {
    if (dev) console.log({ event: "onclose" });
    ws_status.set("disconnected");
    clearInterval(refresher);

    // Start reconnection attempts
    reconnect_web_socket(token, should_refresh_token);
  });

  connection.addEventListener("error", (error) => {
    if (dev) console.log({ event: "onerror", error });
    ws_status.set("error");

    // Start reconnection attempts
    reconnect_web_socket(token, should_refresh_token);
  });

  return connection;
};
